/**
 * 🤖 AUTO-GENERATED API SCHEMA FILE - DO NOT EDIT DIRECTLY!
 *
 * Generated from: sale_transactions entity configuration
 * Generator: scripts/generate-api-clients.ts
 *
 * This file contains the API client for the sale_transactions table with:
 * - Type-safe CRUD operations
 * - Field-level security enforcement
 * - Search functionality with multi-term search and global config integration
 * - Validation using generated schemas
 *
 * @description
 * This client provides a complete interface for interacting with the sale_transactions API.
 * All operations respect the field permissions defined in the entity configuration.
 *
 * Public fields: [id, transaction_type_id, transaction_status_id, source_id, external_ref, asking_price, price_per_sf, price_per_ac, price_per_unit, cap_rate, close_date, listed_date, comments, verified_at, verified_by_user_profile_id, created_by_user_profile_id, updated_by_user_profile_id, created_at, updated_at, permission_level_id, is_colliers_sale, external_key, sale_price, review_status_id, reviewed_at, reviewed_by_user_profile_id, created_by_ingestion_job_id, internal_ref, conditions_of_sale_id, is_investment, archived_at, archived_by_user_profile_id, archive_reason, archive_batch_id]
 * Searchable fields: []
 * Filterable fields: [id, transaction_type_id, transaction_status_id, source_id, verified_by_user_profile_id, created_by_user_profile_id, updated_by_user_profile_id, permission_level_id, review_status_id, reviewed_by_user_profile_id, created_by_ingestion_job_id, conditions_of_sale_id, archived_by_user_profile_id, archive_batch_id]
 */

import { httpClient } from '../http-client.js';
import { type UnifiedQueryOptions } from '@config/search/global-search-config';
import type {
  SaleTransactionsRow,
  SaleTransactionsInsert,
  SaleTransactionsUpdate
} from '../../types/generated_schemas/generated-sale_transactions-schema.js';

export interface SaleTransactionsQueryOptions extends UnifiedQueryOptions {}

export interface SaleTransactionsResponse {
  data: SaleTransactionsRow[];
  count?: number;
  success: boolean;
}

/**
 * API Client for sale_transactions table operations
 *
 * @description Provides type-safe access to sale_transactions API endpoints with
 * field-level security enforcement and comprehensive error handling.
 */
export class SaleTransactionsClient {
  private readonly basePath = '/sale-transactions';

  /**
   * Get all sale_transactions records with optional filtering and pagination
   *
   * @param options - Query options for filtering, sorting, and pagination
   * @returns Promise resolving to array of sale_transactions records
   */
  async getAll(options?: SaleTransactionsQueryOptions): Promise<SaleTransactionsRow[]> {
    const params = new URLSearchParams();
    
    if (options?.limit) params.set('limit', options.limit.toString());
    if (options?.offset) params.set('offset', options.offset.toString());
    if (options?.sortBy) params.set('sort', options.sortBy);
    if (options?.sortOrder) params.set('order', options.sortOrder);
    if (options?.filters) {
      Object.entries(options.filters).forEach(([key, value]) => {
        if (value !== undefined && value !== null) {
          params.set(key, value.toString());
        }
      });
    }

    const queryString = params.toString();
    const url = queryString ? `${this.basePath}?${queryString}` : this.basePath;
    
    const response = await httpClient.get<SaleTransactionsResponse>(url);
    return response.data;
  }

  /**
   * Get a single sale_transactions record by ID
   *
   * @param id - Unique identifier for the record
   * @returns Promise resolving to the sale_transactions record
   */
  async getById(id: string): Promise<SaleTransactionsRow> {
    const response = await httpClient.get<{
      data: SaleTransactionsRow;
      success: boolean;
    }>(`${this.basePath}/${id}`);
    return response.data;
  }

  /**
   * Create a new sale_transactions record
   *
   * @param data - Data for creating the new record
   * @returns Promise resolving to the created sale_transactions record
   */
  async create(data: SaleTransactionsInsert): Promise<SaleTransactionsRow> {
    const response = await httpClient.post<{
      data: SaleTransactionsRow;
      success: boolean;
    }>(this.basePath, data);
    return response.data;
  }

  /**
   * Update an existing sale_transactions record
   *
   * @param id - Unique identifier for the record to update
   * @param data - Updated data for the record
   * @returns Promise resolving to the updated sale_transactions record
   */
  async update(id: string, data: SaleTransactionsUpdate): Promise<SaleTransactionsRow> {
    const response = await httpClient.put<{
      data: SaleTransactionsRow;
      success: boolean;
    }>(`${this.basePath}/${id}`, data);
    return response.data;
  }

  /**
   * Delete a sale_transactions record
   *
   * @param id - Unique identifier for the record to delete
   * @returns Promise resolving when deletion is complete
   */
  async delete(id: string): Promise<void> {
    await httpClient.delete(`${this.basePath}/${id}`);
  }

  /**
   * Create multiple sale_transactions records in a single request
   *
   * @param data - Array of data for creating new records
   * @returns Promise resolving to array of created sale_transactions records
   */
  async createMany(data: SaleTransactionsInsert[]): Promise<SaleTransactionsRow[]> {
    const response = await httpClient.post<{
      data: SaleTransactionsRow[];
      success: boolean;
    }>(`${this.basePath}/batch`, { items: data });
    return response.data;
  }

  /**
   * Search sale_transactions with text query
   * ⚠️  SEARCH DISABLED: No fields marked as searchable in entity config
   */
  async search(
    _query: string,
    _columns?: string[],
    _options?: SaleTransactionsQueryOptions
  ): Promise<SaleTransactionsResponse> {
    throw new Error('Search operations are not available for sale_transactions - no searchable fields configured');
  }

  /**
   * Get count of sale_transactions records matching optional filters
   *
   * @param filters - Optional filters to apply when counting
   * @returns Promise resolving to the count of matching records
   */
  async count(filters?: Record<string, any>): Promise<number> {
    const params = new URLSearchParams();
    
    if (filters) {
      Object.entries(filters).forEach(([key, value]) => {
        if (value !== undefined && value !== null) {
          params.set(key, value.toString());
        }
      });
    }

    const queryString = params.toString();
    const url = queryString ? `${this.basePath}/count?${queryString}` : `${this.basePath}/count`;
    
    const response = await httpClient.get<{ data: number; success: boolean }>(url);
    return response.data;
  }

  /**
   * Check if a sale_transactions record exists by ID
   *
   * @param id - Unique identifier to check
   * @returns Promise resolving to boolean indicating existence
   */
  async exists(id: string): Promise<boolean> {
    try {
      await this.getById(id);
      return true;
    } catch (error) {
      // Assuming 404 means record doesn't exist
      return false;
    }
  }

  /**
   * Get field configuration metadata for this entity
   *
   * @returns Object containing field permission and configuration data
   */
  getFieldConfig() {
    return {
      tableName: 'sale_transactions',
      publicFields: ['id', 'transaction_type_id', 'transaction_status_id', 'source_id', 'external_ref', 'asking_price', 'price_per_sf', 'price_per_ac', 'price_per_unit', 'cap_rate', 'close_date', 'listed_date', 'comments', 'verified_at', 'verified_by_user_profile_id', 'created_by_user_profile_id', 'updated_by_user_profile_id', 'created_at', 'updated_at', 'permission_level_id', 'is_colliers_sale', 'external_key', 'sale_price', 'review_status_id', 'reviewed_at', 'reviewed_by_user_profile_id', 'created_by_ingestion_job_id', 'internal_ref', 'conditions_of_sale_id', 'is_investment', 'archived_at', 'archived_by_user_profile_id', 'archive_reason', 'archive_batch_id'],
      searchableFields: [],
      filterableFields: ['id', 'transaction_type_id', 'transaction_status_id', 'source_id', 'verified_by_user_profile_id', 'created_by_user_profile_id', 'updated_by_user_profile_id', 'permission_level_id', 'review_status_id', 'reviewed_by_user_profile_id', 'created_by_ingestion_job_id', 'conditions_of_sale_id', 'archived_by_user_profile_id', 'archive_batch_id'],
      sortableFields: ['close_date', 'listed_date', 'created_at', 'updated_at'],
      readonlyFields: ['id', 'created_at', 'updated_at', 'archived_at', 'archived_by_user_profile_id', 'archive_reason', 'archive_batch_id'],
      requiredFields: ['id', 'permission_level_id', 'is_colliers_sale'],
    };
  }
}

// Export singleton instance for convenience
export const saleTransactionsClient = new SaleTransactionsClient();
